"use client";

import React from "react";
import { XCircle } from "@/lib/icons";
import { $n } from "@/lib/format";
import { OPTION_MULTIPLIER } from "@/lib/option-multiplier";

export interface SpreadPnlChartProps {
  /** Underlying ticker, shown in the title. */
  underlying: string;
  /** "PUT" => bull put (short higher strike), otherwise bear call. */
  putCall: string;
  shortStrike: number;
  longStrike: number;
  /** |shortStrike − longStrike|. */
  width: number;
  /** Net credit per share — the entry credit for a held spread, or the
   *  current quote mid for a scanned opportunity. Drives the payoff curve
   *  and max profit/loss. */
  netCredit: number;
  contracts: number;
  /** Current underlying spot; draws the amber marker + %-to-short-strike. */
  currentPrice: number | null;
  /** Live current net premium. When provided, the footer shows an
   *  entry/current "Premium/Current" pair; when omitted it shows just the
   *  premium (opportunities have no entry to compare against). */
  currentMid?: number | null;
  /** Current open P&L in dollars (held positions only). When omitted the
   *  "Current P&L" footer row is hidden. */
  currentPnl?: number | null;
  /** Diagram size. Defaults suit the desktop modals; the mobile site passes a
   *  measured width so the profile fits a phone. */
  chartWidth?: number;
  chartHeight?: number;
  onClose: () => void;
}

/**
 * Vertical-spread P&L profile: a hand-rolled SVG payoff diagram plus a small
 * stats footer. Shared by the Open Vertical Spreads / 0DTE position modals
 * (held spreads, with live premium + current P&L) and the Trading
 * Opportunities scan tables (scanned spreads, payoff + max profit/loss only).
 */
export function SpreadPnlChart({
  underlying,
  putCall,
  shortStrike: shortK,
  longStrike: longK,
  width,
  netCredit,
  contracts: qty,
  currentPrice,
  currentMid,
  currentPnl,
  chartWidth,
  chartHeight,
  onClose,
}: SpreadPnlChartProps) {
  const maxProfit = netCredit * qty * OPTION_MULTIPLIER;
  const maxLoss = (width - netCredit) * qty * OPTION_MULTIPLIER;
  const pctDecay = currentPnl != null && maxProfit > 0 ? (currentPnl / maxProfit) * 100 : 0;
  // Build P&L chart points. The x-axis range must include the current
  // underlying price — otherwise the spot marker gets clamped at the edge
  // with an OOB arrow and the chart isn't really to scale. We anchor on the
  // strikes + spot and pad by at least 2× the spread width on each side.
  const refLo = Math.min(shortK, longK, currentPrice ?? Infinity);
  const refHi = Math.max(shortK, longK, currentPrice ?? -Infinity);
  const pad = Math.max(width * 2, (refHi - refLo) * 0.15);
  const lo = refLo - pad;
  const hi = refHi + pad;
  const steps = 40;
  const step = (hi - lo) / steps;
  const points: { price: number; pnl: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const price = lo + i * step;
    let pnl: number;
    if (putCall === "PUT") {
      // Bull put: short higher strike put, long lower strike put
      if (price >= shortK) pnl = netCredit;
      else if (price <= longK) pnl = -(width - netCredit);
      else pnl = netCredit - (shortK - price);
    } else {
      // Bear call: short lower strike call, long higher strike call
      if (price <= shortK) pnl = netCredit;
      else if (price >= longK) pnl = -(width - netCredit);
      else pnl = netCredit - (price - shortK);
    }
    points.push({ price, pnl });
  }
  const chartW = chartWidth ?? 615, chartH = chartHeight ?? 293, padL = 8, padR = 8, padT = 27, padB = 48;
  const drawW = chartW - padL - padR, drawH = chartH - padT - padB;
  const minPnl = Math.min(...points.map(p => p.pnl));
  const maxPnl = Math.max(...points.map(p => p.pnl));
  // Drop the y-domain a little below max loss so the bottom (max-loss) line
  // clears the x-axis and has space beneath it.
  const yMin = minPnl - (maxPnl - minPnl || 1) * 0.12;
  const pnlRange = maxPnl - yMin || 1;
  const x = (price: number) => padL + ((price - lo) / (hi - lo)) * drawW;
  const y = (pnl: number) => padT + drawH - ((pnl - yMin) / pnlRange) * drawH;
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.price).toFixed(1)},${y(p.pnl).toFixed(1)}`).join(" ");
  const zeroY = y(0);
  // "Nice" round tick interval for the price axis — picks 1/2/5×10ᵏ so the
  // chart renders to scale with readable labels, instead of only showing the
  // two strike prices at the extremes.
  const niceStep = (range: number, target = 6) => {
    const raw = range / target;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    const n = raw / mag;
    return (n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10) * mag;
  };
  const tickStep = niceStep(hi - lo);
  const firstTick = Math.ceil(lo / tickStep) * tickStep;
  const xTicks: number[] = [];
  for (let v = firstTick; v <= hi + 1e-9; v += tickStep) xTicks.push(+v.toFixed(6));
  const baselineY = chartH - padB;
  return (
    <div className="text-sm" style={{ width: chartW }}>
      {/* Header + rows are inset by the chart's plot padding so the title,
          close button, and label rows line up with the left and right edges
          of the diagram (not the wider svg). */}
      <div className="flex items-center justify-between mb-3" style={{ paddingLeft: padL, paddingRight: padR }}>
        <span className="font-semibold text-amber text-base">{underlying} P&L Profile</span>
        <button onClick={onClose} className="text-foreground/60 hover:text-foreground"><XCircle className="w-[18px] h-[18px]" /></button>
      </div>
      <svg width={chartW} height={chartH} className="mb-4">
        {/* zero line */}
        <line x1={padL} x2={chartW - padR} y1={zeroY} y2={zeroY} stroke="var(--border)" strokeDasharray="3,3" />
        {/* strike lines */}
        <line x1={x(shortK)} x2={x(shortK)} y1={padT} y2={chartH - padB} stroke="#9ca3af" strokeWidth="1.5" />
        <line x1={x(longK)} x2={x(longK)} y1={padT} y2={chartH - padB} stroke="#9ca3af" strokeWidth="1.5" />
        {/* P&L line */}
        <path d={pathD} fill="none" stroke="var(--cyan)" strokeWidth="2.5" />
        {/* profit zone fill */}
        {(() => {
          const profitPts = points.filter(p => p.pnl > 0);
          if (profitPts.length < 2) return null;
          const fillD = profitPts.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.price).toFixed(1)},${y(p.pnl).toFixed(1)}`).join(" ")
            + ` L${x(profitPts[profitPts.length - 1].price).toFixed(1)},${zeroY.toFixed(1)} L${x(profitPts[0].price).toFixed(1)},${zeroY.toFixed(1)} Z`;
          return <path d={fillD} fill="var(--green)" opacity="0.15" />;
        })()}
        {/* loss zone fill */}
        {(() => {
          const lossPts = points.filter(p => p.pnl < 0);
          if (lossPts.length < 2) return null;
          const fillD = lossPts.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.price).toFixed(1)},${y(p.pnl).toFixed(1)}`).join(" ")
            + ` L${x(lossPts[lossPts.length - 1].price).toFixed(1)},${zeroY.toFixed(1)} L${x(lossPts[0].price).toFixed(1)},${zeroY.toFixed(1)} Z`;
          return <path d={fillD} fill="var(--red)" opacity="0.15" />;
        })()}
        {/* Current price marker — the x-axis range is widened to always
            include the spot, so this is always inside the chart (no
            out-of-range clamp / arrow). */}
        {currentPrice != null && (
          <>
            <line x1={x(currentPrice)} x2={x(currentPrice)} y1={padT} y2={chartH - padB} stroke="var(--amber)" strokeWidth="1.5" strokeDasharray="4,2" />
            <text x={x(currentPrice)} y={padT - 8} textAnchor="middle" fill="#fbbf24" fontSize="11" fontWeight="700" fontFamily="monospace">${$n(currentPrice)}</text>
          </>
        )}
        {/* X-axis: solid baseline + ruler-style price ticks at even intervals
            so the chart is genuinely to scale, instead of only labelling the
            two strike prices. */}
        <line x1={padL} x2={chartW - padR} y1={baselineY} y2={baselineY} stroke="#e5e7eb" strokeWidth="1" />
        {xTicks.map((v) => (
          <g key={`xt-${v}`}>
            <line x1={x(v)} x2={x(v)} y1={padT} y2={baselineY} stroke="#3f3f46" strokeDasharray="2,3" />
            <line x1={x(v)} x2={x(v)} y1={baselineY} y2={baselineY + 6} stroke="#e5e7eb" strokeWidth="1" />
            <text x={x(v)} y={baselineY + 18} textAnchor="middle" fill="#e5e7eb" fontSize="11" fontWeight="600" fontFamily="monospace">{v}</text>
          </g>
        ))}
        {/* % distance from spot to the short strike, centered between the
            current-price marker and the short-strike marker. */}
        {currentPrice != null && currentPrice > 0 && (
          <text
            x={(x(currentPrice) + x(shortK)) / 2}
            y={padT + 13}
            textAnchor="middle"
            fill="#cbd5e1"
            fontSize="10"
            fontFamily="monospace"
          >
            {((Math.abs(shortK - currentPrice) / currentPrice) * 100).toFixed(1)}%
          </text>
        )}
      </svg>
      <div className="flex flex-col gap-[6px]" style={{ paddingLeft: padL, paddingRight: padR }}>
        <div className="flex justify-between">
          <span className="text-foreground/70 font-[var(--font-lora)]">{currentMid != null ? "Premium/Current" : "Premium"}</span>
          <span className="text-foreground">
            {netCredit.toFixed(2)}
            {currentMid != null && (
              <>
                <span className="text-foreground/50">/</span>
                <span style={{ color: currentMid < netCredit ? "#4ade80" : currentMid > netCredit ? "#f87171" : "#e5e7eb" }}>
                  {currentMid.toFixed(2)}
                </span>
              </>
            )}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-foreground/70 font-[var(--font-lora)]">Max Profit/Loss</span>
          <span>
            <span style={{ color: "#4ade80" }}>${$n(maxProfit)}</span>
            <span className="text-foreground/50">/</span>
            <span style={{ color: "#f87171" }}>${$n(maxLoss)}</span>
          </span>
        </div>
        {currentPnl != null && (
          <div className="flex justify-between">
            <span className="text-foreground/70 font-[var(--font-lora)]">Current P&L</span>
            <span style={{ color: currentPnl >= 0 ? "#4ade80" : "#f87171" }}>{currentPnl >= 0 ? "+" : ""}${$n(Math.abs(currentPnl))} ({pctDecay.toFixed(0)}%)</span>
          </div>
        )}
      </div>
    </div>
  );
}
